import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Checkbox,
  Divider,
  Header,
  Icon,
  Input,
  Segment,
  TextArea,
} from "semantic-ui-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../../app/stores/store";
import { useTranslation } from "react-i18next";
import { sportOptions } from "../../app/common/options/sportOptions";
import { GameLocationPicker } from "../../components/map/GameLocationPicker";
import PhotoUploadWidget from "../../app/common/imageUpload/PhotoUploadWidget";
import ReactSelect from "react-select";
import {
  CreateTournamentDto,
  TournamentDto,
  TournamentFormat,
  TournamentRegistrationMode,
  TournamentRosterFreezePolicy,
} from "../../app/models/Tournament";

interface Props {
  tournament?: TournamentDto;
}

const formatOptions: {
  key: TournamentFormat;
  text: string;
  value: TournamentFormat;
  icon: string;
}[] = [
  {
    key: "SingleElimination",
    value: "SingleElimination",
    text: "Single Elimination",
    icon: "sitemap",
  },
  {
    key: "DoubleElimination",
    value: "DoubleElimination",
    text: "Double Elimination",
    icon: "code branch",
  },
  {
    key: "RoundRobin",
    value: "RoundRobin",
    text: "Round Robin (League)",
    icon: "sync",
  },
  {
    key: "GroupStageKnockout",
    value: "GroupStageKnockout",
    text: "Group Stage + Knockout",
    icon: "th",
  },
];

// Konvertuje UTC ISO string u lokalno vreme za datetime-local input
// npr. "2026-06-15T16:00:00.000Z" (UTC) → "2026-06-15T18:00" (za UTC+2 korisnika)
const toLocalDateTimeInput = (utcString?: string | null): string => {
  if (!utcString) return "";
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().substring(0, 16);
};

export default observer(function TournamentForm({ tournament }: Props) {
  const { t } = useTranslation();
  const { tournamentStore } = useStore();
  const navigate = useNavigate();

  const isEdit = !!tournament;
  const [showLogoUpload, setShowLogoUpload] = useState(false);

  const localizedSports = sportOptions.map((o) => ({
    value: o.value,
    label: t(`sports.${o.label.toLowerCase()}`) || o.label,
  }));

  const [form, setForm] = useState<CreateTournamentDto>({
    name: tournament?.name ?? "",
    sportID: tournament?.sportId ?? "",
    format: tournament?.format ?? "SingleElimination",
    minTeams: tournament?.minTeams ?? 4,
    maxTeams: tournament?.maxTeams ?? 16,
    // Edit mode: konvertuj UTC → lokalno za prikaz u inputu
    // Create mode: prazno, korisnik unosi lokalno vreme direktno
    registrationOpensAt: toLocalDateTimeInput(tournament?.registrationOpensAt),
    registrationClosesAt: toLocalDateTimeInput(
      tournament?.registrationClosesAt,
    ),
    startsAt: toLocalDateTimeInput(tournament?.startsAt),
    endsAt: toLocalDateTimeInput(tournament?.endsAt),
    latitude: tournament?.latitude,
    longitude: tournament?.longitude,
    isOnline: tournament?.isOnline ?? false,
    description: tournament?.description ?? "",
    rules: tournament?.rules ?? "",
    entryFee: tournament?.entryFee ?? 0,
    prizePool: tournament?.prizePool ?? "",
    registrationMode: tournament?.registrationMode ?? "AutoAccept",
    rosterFreezePolicy: tournament?.rosterFreezePolicy ?? "Flexible",
    rosterFreezeDaysBeforeStart: tournament?.rosterFreezeDaysBeforeStart ?? 3,
    pointsForWin: tournament?.pointsForWin ?? 3,
    pointsForDraw: tournament?.pointsForDraw ?? 1,
    pointsForLoss: tournament?.pointsForLoss ?? 0,
    numberOfGroups: tournament?.numberOfGroups,
    teamsPerGroup: tournament?.teamsPerGroup,
    teamsAdvancingPerGroup: tournament?.teamsAdvancingPerGroup,
    hasThirdPlaceMatch: tournament?.hasThirdPlaceMatch ?? false,
    hasReferees: tournament?.hasReferees ?? false,
    minRefereesPerMatch: tournament?.minRefereesPerMatch ?? 1,
    refereesCanApply: tournament?.refereesCanApply ?? false,
    organizerPhone: tournament?.organizerPhone ?? "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: keyof CreateTournamentDto, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = t("tournament.validation.nameRequired");
    if (!form.sportID) e.sportID = t("tournament.validation.sportRequired");
    if (!form.registrationOpensAt)
      e.registrationOpensAt = t("tournament.validation.required");
    if (!form.registrationClosesAt)
      e.registrationClosesAt = t("tournament.validation.required");
    if (!form.startsAt) e.startsAt = t("tournament.validation.required");
    if (!form.organizerPhone.trim())
      e.organizerPhone = t("tournament.validation.phoneRequired");
    if (form.minTeams < 2) e.minTeams = t("tournament.validation.minTeams");
    if (form.maxTeams < form.minTeams)
      e.maxTeams = t("tournament.validation.maxTeamsMin");
    if (!form.isOnline && !form.latitude)
      e.location = t("tournament.validation.locationRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (isEdit && tournament) {
      await tournamentStore.updateTournament(tournament.id, form);
      navigate(`/tournaments/${tournament.id}`);
    } else {
      const id = await tournamentStore.createTournament(form);
      if (id) navigate(`/tournaments/${id}`);
    }
  };

  const inputStyle = { marginBottom: 14 };
  const labelStyle: React.CSSProperties = {
    fontWeight: 700,
    display: "block",
    marginBottom: 4,
  };
  const errStyle: React.CSSProperties = {
    color: "#9f3a38",
    fontSize: 12,
    marginTop: 2,
  };

  return (
    <Segment clearing style={{ maxWidth: 760, margin: "0 auto" }}>
      <Header color="teal" as="h2">
        <Icon name="trophy" />
        {isEdit
          ? t("tournament.form.editTitle")
          : t("tournament.form.createTitle")}
      </Header>

      {/* ── Basic Info ──────────────────────────────────────────────────────── */}
      <Header sub color="grey">
        {t("tournament.form.sectionBasic")}
      </Header>

      <div style={inputStyle}>
        <label style={labelStyle}>{t("tournament.form.nameLabel")} *</label>
        <Input
          fluid
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder={t("tournament.form.namePlaceholder")}
          error={!!errors.name}
        />
        {errors.name && <div style={errStyle}>{errors.name}</div>}
      </div>

      <div style={inputStyle}>
        <label style={labelStyle}>{t("tournament.form.sportLabel")} *</label>
        <ReactSelect
          options={localizedSports}
          value={localizedSports.find((o) => o.value === form.sportID) || null}
          onChange={(opt) => set("sportID", opt?.value || "")}
          placeholder={t("tournament.form.sportPlaceholder")}
          isSearchable
          isDisabled={isEdit}
          styles={{
            control: (base) => ({
              ...base,
              borderColor: errors.sportID ? "#9f3a38" : base.borderColor,
              minHeight: 38,
            }),
            menu: (base) => ({ ...base, zIndex: 10 }),
          }}
        />
        {errors.sportID && <div style={errStyle}>{errors.sportID}</div>}
      </div>

      <div style={inputStyle}>
        <label style={labelStyle}>{t("tournament.form.formatLabel")} *</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {formatOptions.map((f) => (
            <Button
              key={f.key}
              toggle
              active={form.format === f.value}
              color={form.format === f.value ? "teal" : undefined}
              basic={form.format !== f.value}
              onClick={() => set("format", f.value)}
              disabled={isEdit}
              size="small"
            >
              <Icon name={f.icon as any} />
              {f.text}
            </Button>
          ))}
        </div>
      </div>

      <div style={inputStyle}>
        <label style={labelStyle}>
          {t("tournament.form.descriptionLabel")}
        </label>
        <TextArea
          rows={3}
          value={form.description}
          onChange={(_, d) => {
            const val = (d.value as string).slice(0, 3000);
            set("description", val);
          }}
          placeholder={t("tournament.form.descriptionPlaceholder")}
          style={{
            width: "100%",
            borderRadius: 4,
            border: "1px solid rgba(34,36,38,.15)",
            padding: 10,
          }}
        />
        <div
          style={{
            textAlign: "right",
            fontSize: 11,
            color: (form.description?.length ?? 0) > 2800 ? "#9f3a38" : "#999",
            marginTop: 2,
          }}
        >
          {form.description?.length ?? 0} / 3000
        </div>
      </div>

      <div style={inputStyle}>
        <label style={labelStyle}>{t("tournament.form.rulesLabel")}</label>
        <TextArea
          rows={4}
          value={form.rules}
          onChange={(_, d) => set("rules", d.value as string)}
          placeholder={t("tournament.form.rulesPlaceholder")}
          style={{
            width: "100%",
            borderRadius: 4,
            border: "1px solid rgba(34,36,38,.15)",
            padding: 10,
          }}
        />
      </div>

      {/* ── Teams ──────────────────────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionTeams")}
      </Header>

      <div
        style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}
      >
        <div style={{ flex: "1 1 120px" }}>
          <label style={labelStyle}>{t("tournament.form.minTeams")} *</label>
          <Input
            type="number"
            min={2}
            value={form.minTeams}
            onChange={(e) => set("minTeams", parseInt(e.target.value) || 2)}
            error={!!errors.minTeams}
          />
          {errors.minTeams && <div style={errStyle}>{errors.minTeams}</div>}
        </div>
        <div style={{ flex: "1 1 120px" }}>
          <label style={labelStyle}>{t("tournament.form.maxTeams")} *</label>
          <Input
            type="number"
            min={form.minTeams}
            value={form.maxTeams}
            onChange={(e) =>
              set("maxTeams", parseInt(e.target.value) || form.minTeams)
            }
            error={!!errors.maxTeams}
          />
          {errors.maxTeams && <div style={errStyle}>{errors.maxTeams}</div>}
        </div>
      </div>

      {/* Group Stage config */}
      {form.format === "GroupStageKnockout" && (
        <Segment secondary>
          <Header sub color="teal">
            {t("tournament.form.groupConfig")}
          </Header>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 100px" }}>
              <label style={labelStyle}>
                {t("tournament.form.numberOfGroups")}
              </label>
              <Input
                type="number"
                min={2}
                value={form.numberOfGroups ?? ""}
                onChange={(e) =>
                  set("numberOfGroups", parseInt(e.target.value) || undefined)
                }
              />
            </div>
            <div style={{ flex: "1 1 100px" }}>
              <label style={labelStyle}>
                {t("tournament.form.teamsPerGroup")}
              </label>
              <Input
                type="number"
                min={2}
                value={form.teamsPerGroup ?? ""}
                onChange={(e) =>
                  set("teamsPerGroup", parseInt(e.target.value) || undefined)
                }
              />
            </div>
            <div style={{ flex: "1 1 120px" }}>
              <label style={labelStyle}>
                {t("tournament.form.teamsAdvancing")}
              </label>
              <Input
                type="number"
                min={1}
                value={form.teamsAdvancingPerGroup ?? ""}
                onChange={(e) =>
                  set(
                    "teamsAdvancingPerGroup",
                    parseInt(e.target.value) || undefined,
                  )
                }
              />
            </div>
          </div>
        </Segment>
      )}

      {/* Round Robin scoring */}
      {(form.format === "RoundRobin" ||
        form.format === "GroupStageKnockout") && (
        <Segment secondary>
          <Header sub color="teal">
            {t("tournament.form.scoringSystem")}
          </Header>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {(["pointsForWin", "pointsForDraw", "pointsForLoss"] as const).map(
              (field) => (
                <div key={field} style={{ flex: "1 1 80px" }}>
                  <label style={labelStyle}>
                    {t(`tournament.form.${field}`)}
                  </label>
                  <Input
                    type="number"
                    min={0}
                    value={form[field]}
                    onChange={(e) => set(field, parseInt(e.target.value) || 0)}
                  />
                </div>
              ),
            )}
          </div>
        </Segment>
      )}

      {/* 3rd place match */}
      {form.format === "SingleElimination" && (
        <div style={inputStyle}>
          <Checkbox
            label={t("tournament.form.thirdPlaceMatch")}
            checked={form.hasThirdPlaceMatch}
            onChange={(_, d) => set("hasThirdPlaceMatch", d.checked)}
          />
        </div>
      )}

      {/* ── Dates ──────────────────────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionDates")}
      </Header>

      <div
        style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}
      >
        {(
          [
            ["registrationOpensAt", t("tournament.form.regOpens")],
            ["registrationClosesAt", t("tournament.form.regCloses")],
            ["startsAt", t("tournament.form.startsAt")],
            ["endsAt", t("tournament.form.endsAt")],
          ] as [keyof CreateTournamentDto, string][]
        ).map(([field, label]) => (
          <div key={field} style={{ flex: "1 1 180px" }}>
            <label style={labelStyle}>
              {label}
              {field !== "endsAt" ? " *" : ""}
            </label>
            <input
              type="datetime-local"
              value={(form[field] as string) ?? ""}
              onChange={(e) => set(field, e.target.value)}
              style={{
                width: "100%",
                padding: "9px 10px",
                borderRadius: 4,
                border: "1px solid rgba(34,36,38,.15)",
              }}
            />
            {errors[field] && <div style={errStyle}>{errors[field]}</div>}
          </div>
        ))}
      </div>

      {/* ── Location ───────────────────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionLocation")}
      </Header>

      <div style={{ marginBottom: 12 }}>
        <Checkbox
          label={t("tournament.form.isOnline")}
          checked={form.isOnline}
          onChange={(_, d) => set("isOnline", d.checked)}
        />
      </div>

      {!form.isOnline && (
        <>
          <GameLocationPicker
            onLocationSelect={(lat, lng) => {
              set("latitude", lat);
              set("longitude", lng);
            }}
            initialLat={form.latitude}
            initialLng={form.longitude}
          />
          {errors.location && <div style={errStyle}>{errors.location}</div>}
        </>
      )}

      {/* ── Entry Fee ──────────────────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionFees")}
      </Header>

      <div
        style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}
      >
        <div style={{ flex: "1 1 150px" }}>
          <label style={labelStyle}>{t("tournament.form.entryFee")}</label>
          <Input
            type="number"
            min={0}
            label={{ basic: true, content: "€" }}
            labelPosition="right"
            value={form.entryFee ?? 0}
            onChange={(e) => set("entryFee", parseFloat(e.target.value) || 0)}
          />
        </div>
        <div style={{ flex: "3 1 250px" }}>
          <label style={labelStyle}>{t("tournament.form.prizePool")}</label>
          <Input
            fluid
            value={form.prizePool ?? ""}
            onChange={(e) => set("prizePool", e.target.value)}
            placeholder={t("tournament.form.prizePoolPlaceholder")}
          />
        </div>
      </div>

      {/* ── Registration & Roster ──────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionRegistration")}
      </Header>

      <div style={inputStyle}>
        <label style={labelStyle}>
          {t("tournament.form.registrationMode")}
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          {(["AutoAccept", "ManualReview"] as TournamentRegistrationMode[]).map(
            (m) => (
              <Button
                key={m}
                toggle
                size="small"
                active={form.registrationMode === m}
                color={form.registrationMode === m ? "teal" : undefined}
                basic={form.registrationMode !== m}
                onClick={() => set("registrationMode", m)}
              >
                <Icon name={m === "AutoAccept" ? "bolt" : "clipboard list"} />
                {t(`tournament.registrationMode.${m.toLowerCase()}`)}
              </Button>
            ),
          )}
        </div>
      </div>

      <div style={inputStyle}>
        <label style={labelStyle}>{t("tournament.form.rosterFreeze")}</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {(
            ["Strict", "Flexible", "Open"] as TournamentRosterFreezePolicy[]
          ).map((p) => (
            <Button
              key={p}
              toggle
              size="small"
              active={form.rosterFreezePolicy === p}
              color={form.rosterFreezePolicy === p ? "orange" : undefined}
              basic={form.rosterFreezePolicy !== p}
              onClick={() => set("rosterFreezePolicy", p)}
            >
              {t(`tournament.rosterFreeze.${p.toLowerCase()}`)}
            </Button>
          ))}
        </div>
        {form.rosterFreezePolicy === "Flexible" && (
          <div style={{ marginTop: 10 }}>
            <label style={labelStyle}>{t("tournament.form.freezeDays")}</label>
            <Input
              type="number"
              min={1}
              value={form.rosterFreezeDaysBeforeStart ?? 3}
              onChange={(e) =>
                set(
                  "rosterFreezeDaysBeforeStart",
                  parseInt(e.target.value) || 3,
                )
              }
              style={{ width: 100 }}
            />
            <span style={{ marginLeft: 8, color: "#666" }}>
              {t("tournament.form.daysBeforeStart")}
            </span>
          </div>
        )}
      </div>

      {/* ── Referees ───────────────────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionReferees")}
      </Header>

      <div style={{ marginBottom: 10 }}>
        <Checkbox
          toggle
          label={t("tournament.form.hasReferees")}
          checked={form.hasReferees}
          onChange={(_, d) => set("hasReferees", d.checked)}
        />
      </div>

      {form.hasReferees && (
        <Segment secondary>
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            <div style={{ flex: "1 1 120px" }}>
              <label style={labelStyle}>
                {t("tournament.form.minRefereesPerMatch")}
              </label>
              <Input
                type="number"
                min={1}
                value={form.minRefereesPerMatch}
                onChange={(e) =>
                  set("minRefereesPerMatch", parseInt(e.target.value) || 1)
                }
              />
            </div>
          </div>
          <Checkbox
            label={t("tournament.form.refereesCanApply")}
            checked={form.refereesCanApply}
            onChange={(_, d) => set("refereesCanApply", d.checked)}
          />
        </Segment>
      )}

      {/* ── Organizer Contact ──────────────────────────────────────────────── */}
      <Divider />
      <Header sub color="grey">
        {t("tournament.form.sectionContact")}
      </Header>

      <div style={inputStyle}>
        <label style={labelStyle}>
          {t("tournament.form.organizerPhone")} *
        </label>
        <Input
          fluid
          type="tel"
          value={form.organizerPhone}
          onChange={(e) => set("organizerPhone", e.target.value)}
          placeholder={t("tournament.form.organizerPhonePlaceholder")}
          error={!!errors.organizerPhone}
        />
        {errors.organizerPhone && (
          <div style={errStyle}>{errors.organizerPhone}</div>
        )}
      </div>

      {/* ── Logo (edit only) ─────────────────────────────────────────────── */}
      {isEdit && (
        <>
          <Divider />
          <Header sub color="grey">
            {t("tournament.form.logo")}
          </Header>
          <Button
            type="button"
            size="small"
            basic
            onClick={() => setShowLogoUpload((v) => !v)}
          >
            <Icon name="upload" />
            {t("tournament.form.uploadLogo")}
          </Button>
          {showLogoUpload && (
            <div style={{ marginTop: 12 }}>
              <PhotoUploadWidget
                loading={tournamentStore.submitting}
                uploadPhoto={async (file) => {
                  const url = await tournamentStore.uploadLogo(
                    tournament!.id,
                    file,
                  );
                  if (url) setShowLogoUpload(false);
                }}
              />
            </div>
          )}
        </>
      )}

      {/* ── Actions ────────────────────────────────────────────────────────── */}
      <Divider />
      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
        <Button
          positive
          loading={tournamentStore.submitting}
          disabled={tournamentStore.submitting}
          onClick={handleSubmit}
        >
          <Icon name={isEdit ? "save" : "trophy"} />
          {isEdit ? t("tournament.form.save") : t("tournament.form.create")}
        </Button>
        <Button basic onClick={() => navigate(-1)}>
          {t("tournament.form.cancel")}
        </Button>
      </div>
    </Segment>
  );
});
